import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import {
  DepartmentSkillFrameworkData,
  SkillDetail,
  SkillFramework,
  SkillFrameworkNodesData,
  SkillFrameworkSelectionRequest,
  SkillFrameworkType,
  SkillSearchData,
  SkillSearchParams,
  SkillsConfiguration,
  SkillsConfigurationRequest,
} from '../../entities/skills-framework.model';
import { DashboardApiService } from '../db-api.service';

const FRAMEWORKS_ENDPOINT = '/v1/issuer/skill_frameworks';
const SELECTIONS_ENDPOINT = '/v1/issuer/skill_framework_selections';
const SKILLS_CONFIGURATION_ENDPOINT = '/v1/issuer/skills_configuration';
const SKILLS_ENDPOINT = '/v1/issuer/skills';

@Injectable({
  providedIn: 'root',
})
export class SkillFrameworksApiService extends DashboardApiService {
  private _externalFrameworks$: Observable<SkillFramework[]> | null = null;

  protected override _getHeaders(method: string, endpoint: string): { [header: string]: string } {
    const headers = super._getHeaders(method, endpoint);
    return {
      ...headers,
      'accredible-department-id': this.embedding.departmentId.toString(),
    };
  }

  getFramework(id: number): Observable<SkillFramework> {
    return this._get(`${FRAMEWORKS_ENDPOINT}/${id}`).pipe(
      map((res) => this._handleResponse(res, 'skill_framework')),
      catchError((res) => this._handleError(res)),
    );
  }

  listFrameworks(type?: SkillFrameworkType): Observable<SkillFramework[]> {
    const endpoint = type ? `${FRAMEWORKS_ENDPOINT}?type=${type}` : FRAMEWORKS_ENDPOINT;

    return this._get(endpoint).pipe(
      map((res) => this._handleResponse(res, 'skill_frameworks')),
      catchError((res) => this._handleError(res)),
    );
  }

  listExternalFrameworks(): Observable<SkillFramework[]> {
    if (!this._externalFrameworks$) {
      this._externalFrameworks$ = this.listFrameworks('external').pipe(shareReplay(1));
    }
    return this._externalFrameworks$;
  }

  invalidateExternalFrameworksCache(): void {
    this._externalFrameworks$ = null;
  }

  listFrameworkSelections(): Observable<DepartmentSkillFrameworkData> {
    return this._get(SELECTIONS_ENDPOINT).pipe(
      map((res) => this._handleResponse(res)),
      catchError((res) => this._handleError(res)),
    );
  }

  createFrameworkSelection(request: SkillFrameworkSelectionRequest): Observable<SkillFramework> {
    return this._post(SELECTIONS_ENDPOINT, { skill_framework_selection: request }).pipe(
      map((res) => this._handleResponse(res, 'skill_framework')),
      catchError((res) => this._handleError(res)),
    );
  }

  getConfiguration(): Observable<SkillsConfiguration> {
    return this._get(SKILLS_CONFIGURATION_ENDPOINT).pipe(
      map((res) => this._handleResponse(res, 'skills_configuration')),
      catchError((res) => this._handleError(res)),
    );
  }

  updateConfiguration(request: SkillsConfigurationRequest): Observable<SkillsConfiguration> {
    return this._put(SKILLS_CONFIGURATION_ENDPOINT, { skills_configuration: request }).pipe(
      map((res) => this._handleResponse(res, 'skills_configuration')),
      catchError((res) => this._handleError(res)),
    );
  }

  listFrameworkNodes(
    frameworkId: number,
    params: { parent_id?: number; page?: number; page_size?: number } = {},
  ): Observable<SkillFrameworkNodesData> {
    const queryString = this._buildQueryString(params);
    const base = `${FRAMEWORKS_ENDPOINT}/${frameworkId}/nodes`;
    const url = queryString ? `${base}?${queryString}` : base;

    return this._get(url).pipe(
      map((res) => this._handleResponse(res)),
      catchError((res) => this._handleError(res)),
    );
  }

  getSkillDetail(skillId: number): Observable<SkillDetail> {
    return this._get(`${SKILLS_ENDPOINT}/${skillId}`).pipe(
      map((res) => this._handleResponse(res, 'skill')),
      catchError((res) => this._handleError(res)),
    );
  }

  searchSkills(frameworkId: number, params: SkillSearchParams): Observable<SkillSearchData> {
    const url = `${FRAMEWORKS_ENDPOINT}/${frameworkId}/skills/search?${this._buildQueryString(params)}`;

    return this._get(url).pipe(
      map((res) => this._handleResponse(res)),
      catchError((res) => this._handleError(res)),
    );
  }

  private _buildQueryString<T extends object>(params: T): string {
    const query = new URLSearchParams();

    for (const [key, value] of Object.entries(params)) {
      if (value != null) {
        query.set(key, String(value));
      }
    }

    return query.toString();
  }
}
